import { useState } from "react";
import Modal from "react-modal";
import { cn } from "../../lib/cn";

Modal.setAppElement("#root");

interface CheckStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CheckStatusModal({ isOpen, onClose }: CheckStatusModalProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    exists: boolean;
    checked: boolean;
  }>({ exists: false, checked: false });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://us-central1-league-life-prod.cloudfunctions.net/doesEmailExist?email=${encodeURIComponent(
          email
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to check email status");
      }

      const data = await response.json();
      setResult({ exists: data.exists, checked: true });
    } catch (err) {
      setError("An error occurred. Please try again later.");
      console.error("Error checking email:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setResult({ exists: false, checked: false });
    setError("");
    onClose();
  };

  const handleRequestAccess = () => {
    window.open("https://abi38wtuwac.typeform.com/to/Q3gHPxSM", "_blank");
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={cn([
        "fixed inset-0 bg-[#F1EBE2] w-full h-full overflow-y-auto outline-none",
        "px-[60px] py-[80px]",
        "md:px-[30px] md:py-[60px]",
      ])}
      overlayClassName="fixed inset-0 z-[9999]"
      contentLabel="Check Status Modal"
    >
      <button
        onClick={handleClose}
        className={cn(["absolute top-6 right-6 text-black"])}
        aria-label="Close modal"
      >
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div className="flex flex-col items-center justify-center text-center min-h-full">
        {/* Logo */}
        <div className="flex flex-col items-center mb-[30px]">
          <img
            src="/navbar/logo.svg"
            className="mb-[15px]"
            alt="League Life Logo"
            style={{
              width: "242.36px",
              height: "32.55px",
              filter:
                "brightness(0) saturate(100%) invert(21%) sepia(93%) saturate(2877%) hue-rotate(220deg) brightness(91%) contrast(101%)",
            }}
          />
          <div className="w-[1px] h-[106px] bg-black"></div>
        </div>

        <h3 className="font-montreal text-[32px] font-medium leading-[120%] text-black m-0 mb-[40px] capitalize">
          Check Status
        </h3>

        {!result.checked ? (
          <>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col items-center gap-[20px] w-full max-w-[500px]"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter Your Email"
                className={cn([
                  "w-full max-w-[360px] h-[88px] px-[30px] rounded-lg text-[18px] text-[#999]",
                  "bg-white border-none outline-none text-center font-roboto mb-[20px]",
                  "placeholder:text-[#999] focus:shadow-[0_0_0_2px_rgba(13,52,191,0.3)]",
                  "md:px-[25px] md:text-[16px]",
                ])}
                disabled={isLoading}
                aria-label="Email address"
              />
              {error && (
                <p
                  className={cn([
                    "text-[#d32f2f] text-[14px] -mt-[10px] mb-[10px] font-roboto",
                    "bg-white/95 px-[16px] py-[8px] rounded border border-[#d32f2f]",
                  ])}
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                className={cn([
                  "w-[200px] h-[55px] rounded-full text-[20.36px] font-black leading-[120%]",
                  "text-center flex justify-center items-center border-none cursor-pointer",
                  "transition-all duration-200 font-roboto uppercase",
                  "bg-[#0d34bf] text-white",
                  "hover:opacity-90 hover:-translate-y-[1px] active:translate-y-0",
                  isLoading && "opacity-50 cursor-not-allowed",
                ])}
                disabled={isLoading}
              >
                {isLoading ? "CHECKING..." : "CHECK EMAIL"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className={cn([
                  "w-[200px] h-[55px] rounded-full text-[20.36px] font-black leading-[120%]",
                  "text-center flex justify-center items-center border-none cursor-pointer",
                  "transition-all duration-200 font-roboto uppercase",
                  "bg-black text-white",
                  "hover:opacity-90 hover:-translate-y-[1px] active:translate-y-0",
                ])}
              >
                CLOSE
              </button>
            </form>
          </>
        ) : result.exists ? (
          <div className="flex flex-col items-center justify-center w-full">
            <p className="font-roboto text-[24px] font-normal leading-[1.4] text-black mb-[20px] max-w-[600px] md:text-[20px] md:mb-[15px]">
              Congratualations! You Already Have Access To League Life.
            </p>
            <p className="font-roboto text-[16px] font-normal leading-[1.4] text-[#666] mb-[30px] max-w-[600px] md:text-[14px] md:mb-[20px]">
              Download The App Now Using The Link Below.
            </p>
            <div className="mb-[25px]">
              <img
                src="/footer/league-life-logo.png"
                alt="League Life App Icon"
                className="w-[100px] h-[100px] rounded-[20px] shadow-[0_4px_12px_rgba(0,0,0,0.15)] md:w-[80px] md:h-[80px]"
              />
            </div>
            <a
              href="https://apps.apple.com/us/app/league-life/id1612547898"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-[25px] transition-transform duration-200 hover:-translate-y-[2px]"
            >
              <img
                src="/footer/applestore-logo.png"
                alt="Download on the App Store"
                className="w-[180px] h-auto md:w-[150px]"
              />
            </a>
            <button
              onClick={handleClose}
              className={cn([
                "w-[200px] h-[55px] rounded-full text-[20.36px] font-black leading-[120%]",
                "text-center flex justify-center items-center border-none cursor-pointer",
                "transition-all duration-200 font-roboto uppercase",
                "bg-black text-white",
                "hover:opacity-90 hover:-translate-y-[1px] active:translate-y-0",
              ])}
            >
              CLOSE
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full">
            <p className="font-montreal text-[24px] font-medium leading-[120%] text-black mb-[20px] max-w-[600px] capitalize">
              You Have Not Yet Been Invited To Use League Life.
            </p>
            <button
              onClick={handleRequestAccess}
              className={cn([
                "w-[200px] h-[55px] rounded-full text-[20.36px] font-black leading-[120%]",
                "text-center flex justify-center items-center border-none cursor-pointer",
                "transition-all duration-200 font-roboto uppercase",
                "bg-[#0d34bf] text-white mb-[15px]",
                "hover:opacity-90 hover:-translate-y-[1px] active:translate-y-0",
              ])}
            >
              REQUEST ACCESS
            </button>
            <button
              onClick={handleClose}
              className={cn([
                "w-[200px] h-[55px] rounded-full text-[20.36px] font-black leading-[120%]",
                "text-center flex justify-center items-center border-none cursor-pointer",
                "transition-all duration-200 font-roboto uppercase",
                "bg-black text-white",
                "hover:opacity-90 hover:-translate-y-[1px] active:translate-y-0",
              ])}
            >
              CLOSE
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export { CheckStatusModal };
